import { NextSeo, WebPageJsonLd } from "next-seo";
import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import {
    Filter,
    FilterOperators,
    QueryResponse,
} from "~/modules/filters/types";
import { Routes } from "~/routes";
import { HomePage } from "~/modules/main/pages/home/component";
import {
    TimeSeriesItem,
    TokenTimeseriesColumns,
} from "~/modules/token_timeseries/types";
import {
    ASSET_CLASS_IDS,
    BROADRIDGE_PLATFORM_NAME,
    CANTON_NETWORK_NAME,
    MEASURE_IDS,
    REPURCHASE_AGREEMENTS_ASSET_CLASS_NAME,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { getHomePageSeo } from "~/modules/app/seo/getHomePageSeo";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { GigatableRow } from "~/modules/main/pages/home/GlobalAssetTable/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import {
    LeagueTableGroupTypes,
    LeagueTableTab,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    SEVEN_DAYS_AGO,
    THIRTY_DAYS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { TopTransfersData } from "~/modules/transactions/components/TopTransfersCard/types";
import {
    PlatformSnapshot,
    transformPlatformSnapshotsToTimeSeries,
} from "~/modules/platforms/pages/broadridge_individual/facts_platform_snapshots_data";
import { queryPlatformSnapshots } from "~/modules/platforms/api/router";
import { getLastTimeseriesValue } from "~/utils/getLastTimeseriesValue";
import { getTopTransfers } from "~/modules/transactions/api/getTopTransfers";
import { getOverviewSummaryData } from "~/modules/main/pages/home/getOverviewSummaryData";
import { getTickerMarqueeData } from "~/modules/asset_classes/api/getTickerMarqueeData";
import { AssetTicker } from "~/modules/assets/components/AssetTicker";
import type { TickerGroup } from "~/modules/assets/components/AssetTicker";
import { queryTokenAggregateLeagueTable } from "~/modules/league_tables/api/queryTokenAggregateLeagueTable";

// // // //

function shimLeagueTableDataWithBroadridgeAssetCount(props: {
    leagueTableTabs: LeagueTableTab[];
    broadridgeAssetCount: number;
}) {
    const { leagueTableTabs, broadridgeAssetCount } = props;

    return leagueTableTabs.map((tab) => {
        if (tab.data.groupType === LeagueTableGroupTypes.platforms) {
            tab.data.rows = tab.data.rows.map((row) => {
                if (row.group.name !== BROADRIDGE_PLATFORM_NAME) {
                    return row;
                }
                return { ...row, asset_count: broadridgeAssetCount };
            });
        }

        if (tab.data.groupType === LeagueTableGroupTypes.parentNetworks) {
            tab.data.rows = tab.data.rows.map((row) => {
                if (row.group.name !== CANTON_NETWORK_NAME) {
                    return row;
                }
                return { ...row, asset_count: broadridgeAssetCount };
            });
        }

        if (tab.data.groupType === LeagueTableGroupTypes.assetClasses) {
            tab.data.rows = tab.data.rows.map((row) => {
                if (row.group.name !== REPURCHASE_AGREEMENTS_ASSET_CLASS_NAME) {
                    return row;
                }
                return { ...row, asset_count: broadridgeAssetCount };
            });
        }

        return tab;
    });
}

// // // //

// ENHANCEMENT - get a better pattern together for this Filter, or resolve in the API
const TREASURY_TOKENS_FILTER: Filter = {
    operator: FilterOperators.Or,
    filters: [
        {
            operator: FilterOperators.And,
            filters: [
                {
                    operator: FilterOperators.Equals,
                    field: TokenTimeseriesColumns.assetClassID,
                    value: ASSET_CLASS_IDS.TreasuryProduct,
                },
                {
                    operator: FilterOperators.Equals,
                    field: TokenTimeseriesColumns.isInvestable,
                    value: true,
                },
            ],
        },
        {
            operator: FilterOperators.NotEquals,
            field: TokenTimeseriesColumns.assetClassID,
            value: ASSET_CLASS_IDS.TreasuryProduct,
        },
    ],
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Make all requests in parallel + destructure responses
    const [
        overviewSummary,
        tickerGroups,
        // 7-day transactions
        transactions_7d_under100k,
        transactions_7d_under1m,
        transactions_7d_under10m,
        transactions_7d_under_100m,
        // // // //
        // 30-day transactions
        transactions_30d_under100k,
        transactions_30d_under1m,
        transactions_30d_under10m,
        transactions_30d_under_100m,
        // // // //
        // Broadridge DLR snapshots
        platformSnapshots,
        // League Tables
        leagueTableDistributed,
        leagueTableRepresented,
        leagueTableDistributedAndRepresented,
    ] = await Promise.all([
        getOverviewSummaryData(),
        getTickerMarqueeData(),
        // 7-day transactions
        getTopTransfers({
            name: "home-page-7d-under-100k",
            max: 100_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-7d-under-1m",
            max: 1_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-7d-under-10m",
            max: 10_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-7d-under-100m",
            timestamp: SEVEN_DAYS_AGO,
        }),
        // // // //
        // 30-day transactions
        getTopTransfers({
            name: "home-page-30d-under-100k",
            max: 100_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-30d-under-1m",
            max: 1_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-30d-under-10m",
            max: 10_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "home-page-30d-under-100m",
            timestamp: THIRTY_DAYS_AGO,
        }),

        // Broadridge DLR snapshots
        queryPlatformSnapshots(),

        // League Table - Distributed
        queryTokenAggregateLeagueTable({
            name: "home-page-distributed",
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        {
                            operator: FilterOperators.Equals,
                            field: TokenTimeseriesColumns.tokenizationTypeID,
                            value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                        },
                        {
                            operator: FilterOperators.NotEquals,
                            field: TokenTimeseriesColumns.assetClassID,
                            value: ASSET_CLASS_IDS.Stablecoin,
                        },
                        TREASURY_TOKENS_FILTER,
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.parentNetworks,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
            ],
        }),

        // League Table - Represented
        queryTokenAggregateLeagueTable({
            name: "home-page-represented",
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        {
                            operator: FilterOperators.Equals,
                            field: TokenTimeseriesColumns.tokenizationTypeID,
                            value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                        },
                        {
                            operator: FilterOperators.NotEquals,
                            field: TokenTimeseriesColumns.assetClassID,
                            value: ASSET_CLASS_IDS.Stablecoin,
                        },
                        TREASURY_TOKENS_FILTER,
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.parentNetworks,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
            ],
        }),

        // League Table - Distributed + Represented
        queryTokenAggregateLeagueTable({
            name: "home-page-distributed-and-represented",
            query: {
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        {
                            operator: FilterOperators.NotEquals,
                            field: TokenTimeseriesColumns.assetClassID,
                            value: ASSET_CLASS_IDS.Stablecoin,
                        },
                        TREASURY_TOKENS_FILTER,
                    ],
                },
            },
            groups: [
                LeagueTableGroupTypes.parentNetworks,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
            ],
        }),
    ]);

    const { aggregates, gigatableRows } = overviewSummary;

    // Get Broadridge DLR asset count (Daily Repo Trade Count)
    const broadridgeAssetCountTimeSeries: QueryResponse<TimeSeriesItem> =
        transformPlatformSnapshotsToTimeSeries(
            platformSnapshots.data.results.filter((s: PlatformSnapshot) => {
                return s.measure_id === MEASURE_IDS.TotalContracts;
            }),
        );

    // Get latest value from broadridgeAssetCountTimeSeries
    const broadridgeAssetCount = getLastTimeseriesValue(
        broadridgeAssetCountTimeSeries.results[0],
    );

    // Shim broadridgeAssetCount into LeagueTableTabs
    const leagueTableTabsDistributed =
        shimLeagueTableDataWithBroadridgeAssetCount({
            leagueTableTabs: leagueTableDistributed,
            broadridgeAssetCount,
        });

    const leagueTableTabsRepresented =
        shimLeagueTableDataWithBroadridgeAssetCount({
            leagueTableTabs: leagueTableRepresented,
            broadridgeAssetCount,
        });

    const leagueTableTabsDistributedAndRepresented =
        shimLeagueTableDataWithBroadridgeAssetCount({
            leagueTableTabs: leagueTableDistributedAndRepresented,
            broadridgeAssetCount,
        });

    return {
        props: {
            gigatableRows,
            aggregates,
            tickerGroups,
            leagueTableTabs: {
                all: leagueTableTabsDistributedAndRepresented,
                distributed: leagueTableTabsDistributed,
                represented: leagueTableTabsRepresented,
            },
            topTransfers: {
                seven_day: {
                    under_100k: transactions_7d_under100k,
                    under_1m: transactions_7d_under1m,
                    under_10m: transactions_7d_under10m,
                    under_100m: transactions_7d_under_100m,
                },
                thirty_day: {
                    under_100k: transactions_30d_under100k,
                    under_1m: transactions_30d_under1m,
                    under_10m: transactions_30d_under10m,
                    under_100m: transactions_30d_under_100m,
                },
            },
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export interface OverviewProps {
    gigatableRows: GigatableRow[];
    aggregates: AggregateDisplayProps[];
    tickerGroups: TickerGroup[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    topTransfers: TopTransfersData;
}

export default function Overview(props: OverviewProps & { pathname?: Routes }) {
    const {
        gigatableRows,
        aggregates,
        tickerGroups,
        leagueTableTabs,
        topTransfers,
        pathname = Routes.overview,
    } = props;

    return (
        <PageTrack route={Routes.overview}>
            <NextSeo
                canonical={addBaseUrl(pathname)}
                title="Analytics on Tokenized Real-World Assets"
            />
            <BreadcrumbSEO items={[]} />
            <WebPageJsonLd {...getHomePageSeo({ assetClasses: [] })} />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.overview} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={pathname}>
                <AssetTicker groups={tickerGroups} />
                <PageWrapper>
                    <HomePage
                        gigatableRows={gigatableRows}
                        aggregates={aggregates}
                        leagueTableTabs={leagueTableTabs}
                        topTransfers={topTransfers}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
